import {useId} from 'react';

import {cn} from '../../lib/utils';
import {RadioGroupItem} from '../shadcn/radio-group';
import {Typography} from '../Typography';

import type {ReactNode} from 'react';

const TEXT_SIZES = {
  md: {title: 'md', body: 'sm'},
  sm: {title: 'sm', body: 'xs'}
} as const;

export interface RadioCardProps {
  value: string;
  title: string;
  description: string;
  isSelected: boolean;
  isDisabled?: boolean;
  /** `md` — title and description one step larger than `sm`. */
  size?: 'sm' | 'md';
  /** Rendered after the title, e.g. a lock icon. */
  titleEnd?: ReactNode;
  /** Opposite the description: muted text for a string, e.g. a permission count, or a node such as a badge. */
  aside?: ReactNode;
  /** Aligns the description under the title instead of under the radio. */
  isDescriptionIndented?: boolean;
  /** Below the header and outside the label, so it may hold interactive controls. */
  footer?: ReactNode;
  className?: string;
}

/** Must be rendered inside a `RadioGroup`; the selected state is driven by `isSelected`. */
export function RadioCard({
  value,
  title,
  description,
  isSelected,
  isDisabled = false,
  size = 'md',
  titleEnd,
  aside,
  isDescriptionIndented = false,
  footer,
  className
}: RadioCardProps) {
  const id = useId();
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;
  const {title: titleSize, body: bodySize} = TEXT_SIZES[size];

  return (
    <div
      data-slot="radio-card"
      className={cn(
        'flex flex-col rounded-lg border bg-background-surface',
        isDisabled && 'bg-background-secondary',
        isSelected ? 'choice-selected' : 'border-border',
        className
      )}
    >
      <label
        data-slot="card-header"
        className={cn(
          'flex flex-col gap-1 p-4',
          footer && 'pb-3',
          isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
        )}
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem
            value={value}
            disabled={isDisabled}
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            className="shrink-0"
          />
          <Typography
            id={titleId}
            size={titleSize}
            weight="semibold"
            color={isDisabled ? 'disabled' : undefined}
            className="flex-1"
          >
            {title}
          </Typography>
          {titleEnd}
        </div>
        <div
          className={cn('flex items-start justify-between gap-4', isDescriptionIndented && 'ps-6')}
        >
          <Typography
            id={descriptionId}
            size={bodySize}
            color={isDisabled ? 'disabled' : 'secondary'}
            className="whitespace-normal"
          >
            {description}
          </Typography>
          {typeof aside === 'string' ? (
            <Typography size={bodySize} color="tertiary" className="shrink-0 whitespace-nowrap">
              {aside}
            </Typography>
          ) : (
            aside && (
              <div data-slot="card-aside" className="shrink-0">
                {aside}
              </div>
            )
          )}
        </div>
      </label>
      {footer && (
        <div data-slot="card-footer" className="mx-4 mb-4 border-t border-border pt-3">
          {footer}
        </div>
      )}
    </div>
  );
}
